/**
 * @ngdoc service
 * @name appInsightsService
 * @module flowingly.services
 *
 * @description App insights service
 *
 * ###API
 * * startAppInsights - Initialise app insights so logging can start
 * * trackPageView - Log a page view event
 * * trackMetric - Log a metric
 * * startEventTimer - Start a timer for the named event
 */

import angular, { IPromise } from 'angular';
import {
  ApplicationInsights,
  ICustomProperties,
  IPageViewTelemetry
} from '@microsoft/applicationinsights-web';
import { Guid } from '@Shared.Angular/@types/guid';

export type AppInsightsConfig = {
  instrumentationKey: string;
  connectionString: string;
  [key: string]: string | number | boolean | string[] | null | undefined;
};

type AppInsights = InstanceType<typeof ApplicationInsights>;
type Properties = IPageViewTelemetry['properties'];

// map event name to metric name
const eventToMetricMap = {
  flowStarted: 'flow-started',
  flowStartedForBulkActors: 'flow-started', // flowStarted and flowStartedForBulkActors under same metric flow-started
  flowSelectedFromList: 'flow-loaded',
  flowLoadFromExternalLink: 'flow-loaded-by-link',
  flowsActiveEntered: 'flows-active-loaded',
  flowsTodoEntered: 'flows-todo-loaded',
  flowsInEntered: 'flows-in-loaded',
  flowLoadProcessMapList: 'processmap-list-loaded',
  flowRenderProcessMap: 'processmap-loaded',
  stepFormSubmitted: 'flow-step-form-completed', // click submit on a step form, and see the toast Step completed
  stepFormTransitionStarted: 'flow-next-step-transitioned', // user click Submit a step form, and transitioned to next step form
  stepFormSaved: 'flow-step-form-saved', // user click Save button on a step form, and see the toast Changes successfully saved
  publicFormSubmitted: 'flow-public-form-completed',
  stepTaskDoneSubmitted: 'flow-step-task-done',
  stepTaskApprovalSubmitted: 'flow-step-task-approval-done',
  stepReassigned: 'flow-step-reassigned',
  stepTaskReassigned: 'flow-step-task-reassigned',
  flowCanceled: 'flow-cancelled',
  stepTaskCanceled: 'flow-step-task-cancelled',
  stepUserNudged: 'flow-step-user-nudged',
  subcategoriesFetched: 'categories-with-subcategories-fetched',
  subcategoryDeleteWarning: 'delete-warnings-for-subcategories-fetched',
  subcategoriesdeleted: 'subcategory-deleted',
  categoryEdited: 'category-edited',
  subcategoryAdded: 'subcategory-added',
  userInviteLoaded: 'user-invite-loaded',
  userInviteBusinessSettingsLoaded: 'user-invite-business-settings-loaded',
  userInviteProfileCompleted: 'user-invite-profile-completed',
  governanceLoadProcessReviewDueList:
    'governance-process-review-due-list-loaded',
  governanceLoadMyApprovalsList: 'governance-my-approval-list-loaded',
  governanceSentForApprovalList: 'governance-sent-for-approval-list-loaded',
  setupTaggroupsLoaded: 'setup-taggroups-loaded',
  setupTaggroupsDeleteWarning: 'setup-taggroups-delete-warning',
  setupTaggroupsDeleted: 'setup-taggroups-deleted',
  setupTaggroupsSaved: 'setup-taggroups-saved',
  workflowReportsListLoaded: 'workflow-reports-list-loaded',
  processMapRenderForPrint: 'processmap-render-for-print',
  flowModelAutosave: 'flowmodel-autosave'
} as const;
// app insights name of event
export type EventName = keyof typeof eventToMetricMap;
// app insights name of metrics
export type MetricName = (typeof eventToMetricMap)[EventName];

type EventTimers = Record<EventName, number> | Record<string, never>;
type UserActivityTimed = Record<EventName, number> | Record<string, never>;

interface IAppInsightsCustomProperties extends ICustomProperties {
  userId: Guid | null;
  businessId: Guid | null;
  businessName: string | null;
}

export class AppInsightsService {
  private eventTimers: EventTimers = {};
  private userActivityTimed: UserActivityTimed = {};
  private appInsights: AppInsights;
  private customProperties: IAppInsightsCustomProperties;

  public startAppInsights(
    cfg: AppInsightsConfig,
    customProps: IAppInsightsCustomProperties
  ): void {
    const { instrumentationKey, connectionString, ...restOfConfig } = cfg;

    const config = {
      connectionString:
        connectionString || 'InstrumentationKey=' + instrumentationKey,
      ...restOfConfig
    };

    this.appInsights = new ApplicationInsights({
      config
    });

    this.appInsights.loadAppInsights();
    this.customProperties = customProps;
    this.appInsights.addTelemetryInitializer((envelope) => {
      envelope.data = {
        ...envelope.data,
        ...this.customProperties
      };
    });
    this.appInsights.trackPageView();
  }

  public updateCustomProperties(
    customProps: Partial<IAppInsightsCustomProperties>
  ) {
    this.customProperties = {
      ...this.customProperties,
      ...customProps
    };
  }

  public trackPageView(name: string, properties: Properties): void {
    if (!this.appInsights) {
      return;
    }
    try {
      this.appInsights.trackPageView({
        name,
        properties
      });
    } catch {
      /* empty */
    }
  }

  public trackMetric(
    name: MetricName,
    average: number,
    properties: ICustomProperties = {}
  ): void {
    if (!this.appInsights) {
      return;
    }
    try {
      this.appInsights.trackMetric({
        name,
        average,
        properties
      });
    } catch {
      /* empty */
    }
  }

  public trackTrace(message: string, properties: ICustomProperties): void {
    if (!this.appInsights) {
      return;
    }
    try {
      this.appInsights.trackTrace({
        message,
        properties
      });
    } catch {
      /* empty */
    }
  }

  public startEventTimer(eventName: EventName): void {
    this.resetAllTimer(eventName);
    this.eventTimers[eventName] = Date.now();
  }

  private resetAllTimer(eventName: EventName): void {
    this.eventTimers[eventName] = undefined;
    this.userActivityTimed[eventName] = undefined;
  }

  private getElapsedTimeInSeconds(eventName: EventName): number | undefined {
    const startTime = this.eventTimers[eventName];
    const userActivitiyTimeTaken = this.userActivityTimed[eventName] ?? 0;

    if (startTime) {
      return (Date.now() - startTime - userActivitiyTimeTaken) / 1000;
    }
  }

  public trackMetricIfTimerExist(
    eventName: EventName,
    properties?: ICustomProperties
  ): void {
    const metricName = eventToMetricMap[eventName];
    const elapsedInSeconds = this.getElapsedTimeInSeconds(eventName);

    if (elapsedInSeconds && metricName) {
      this.resetAllTimer(eventName);
      this.trackMetric(metricName, elapsedInSeconds, properties);
    }
  }

  private addUserActivityToEvent(eventName: EventName, timeMs: number): void {
    const currentTimeMs = this.userActivityTimed[eventName] ?? 0;
    this.userActivityTimed[eventName] = timeMs + currentTimeMs;
  }

  public timeUserActivityForEvent<T = unknown>(
    fn: (...params: unknown[]) => IPromise<T>,
    names: EventName | EventName[]
  ) {
    return (...rest: unknown[]) => {
      const startTime = Date.now();
      return fn(...rest).then((result) => {
        const elapsedTime = Date.now() - startTime;
        const eventNames = Array.isArray(names) ? names : [names];

        eventNames.forEach((eventName) => {
          this.addUserActivityToEvent(eventName, elapsedTime);
        });

        return result;
      });
    };
  }
}

angular
  .module('flowingly.services')
  .service('appInsightsService', AppInsightsService);

export type AppInsightsServiceType = InstanceType<typeof AppInsightsService>;
